import React, { useState } from 'react';
import { getTeamImageUrl } from '../constants/config';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import { useI18n } from '../utilities/i18n';
import {
  FORMATIONS_BY_FORMAT,
  MATCH_FORMATS,
  POSITION_LABELS,
  fieldDensityClass,
  generateFieldPositions,
} from '../utilities/lineupSetup';
import '../styles/onlineMatch.css';

export default function MatchLineupSetup({
  step,
  onStepChange,
  matchData,
  matchApplication,
  value,
  onChange,
  onCancel,
  onSubmit,
  submitLabel,
  cancelLabel,
  notify,
  homeFromCaptain,
  guestFromCaptain,
}) {
  const { t } = useI18n();
  const [pickingSlot, setPickingSlot] = useState(null);
  const [draggingPlayer, setDraggingPlayer] = useState(null);
  const [dragOverTarget, setDragOverTarget] = useState(null);

  const homeTeamId = matchData?.home_team_id;
  const guestTeamId = matchData?.guest_team_id;
  const {
    selectedPlayers, matchFormat, homeFormation, guestFormation,
    homeLineup, guestLineup, homeColor, guestColor,
  } = value;

  const showNotif = (message) => { if (notify) notify(message); };
  const teamLogo = (image) => getTeamImageUrl(image) || defaultTeamLogo;

  const getPositionShort = (player, slotIndex) => {
    if (slotIndex === 0) return t('posGK');
    if (player?.position_id) return t(POSITION_LABELS[player.position_id] || '') || '';
    if (player?.match_position) return t(POSITION_LABELS[player.match_position] || '') || '';
    return '';
  };

  const getTeamPlayers = (teamId) => (matchApplication || [])
    .filter((player) => player.team_id === teamId || player.isDouble === teamId);
  const getSelectedTeamPlayers = (teamId) => getTeamPlayers(teamId).filter((player) => selectedPlayers[player.id]);
  const getSelectedCount = (teamId) => getSelectedTeamPlayers(teamId).length;
  const findPlayerById = (id) => (matchApplication || []).find((player) => player.id === id) || null;

  const togglePlayer = (id) => onChange({ selectedPlayers: { ...selectedPlayers, [id]: !selectedPlayers[id] } });

  const selectAll = (teamId) => {
    const players = getTeamPlayers(teamId);
    const allSelected = players.every((player) => selectedPlayers[player.id]);
    const update = { ...selectedPlayers };
    players.forEach((player) => { update[player.id] = !allSelected; });
    onChange({ selectedPlayers: update });
  };

  const goToLineup = () => {
    if (getSelectedCount(homeTeamId) < 1 || getSelectedCount(guestTeamId) < 1) {
      showNotif(t('olmNeedPlayers'));
      return;
    }
    onStepChange('lineup');
  };

  const onFormatChange = (format) => onChange({
    matchFormat: format,
    homeFormation: '',
    guestFormation: '',
    homeLineup: {},
    guestLineup: {},
  });

  const onFormationChange = (side, formation) => onChange(side === 'home'
    ? { homeFormation: formation, homeLineup: {} }
    : { guestFormation: formation, guestLineup: {} });

  const getLineup = (side) => (side === 'home' ? homeLineup : guestLineup);
  const setLineupSide = (side, lineup) => onChange(side === 'home' ? { homeLineup: lineup } : { guestLineup: lineup });
  const getFormation = (side) => (side === 'home' ? homeFormation : guestFormation);

  const getBenchPlayers = (side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const assignedIds = new Set(Object.values(getLineup(side)));
    return getSelectedTeamPlayers(teamId).filter((player) => !assignedIds.has(player.id));
  };

  const handleSlotClick = (side, slotIndex) => {
    const lineup = getLineup(side);
    if (lineup[slotIndex]) {
      const updated = { ...lineup };
      delete updated[slotIndex];
      setLineupSide(side, updated);
      setPickingSlot(null);
    } else {
      setPickingSlot({ teamSide: side, slotIndex });
    }
  };

  const assignPlayerToSlot = (side, slotIndex, playerId) => {
    const updated = { ...getLineup(side) };
    Object.keys(updated).forEach((key) => { if (updated[key] === playerId) delete updated[key]; });
    updated[slotIndex] = playerId;
    setLineupSide(side, updated);
    setPickingSlot(null);
  };

  const autoFillLineup = (side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const formation = getFormation(side);
    if (!formation) return;
    const positions = generateFieldPositions(formation);
    const players = getSelectedTeamPlayers(teamId);
    const lineup = {};
    positions.forEach((_, index) => { if (index < players.length) lineup[index] = players[index].id; });
    setLineupSide(side, lineup);
    setPickingSlot(null);
  };

  const submit = () => {
    if (!matchFormat) { showNotif(t('olmNeedFormat')); return; }
    if (!homeFormation || !guestFormation) { showNotif(t('olmNeedFormation')); return; }
    onSubmit();
  };

  const renderTeamRoster = (teamId, teamName, teamImage) => {
    const players = getTeamPlayers(teamId);
    const count = getSelectedCount(teamId);
    return (
      <div className="olm-roster-team">
        <div className="olm-roster-team-header">
          <img src={teamLogo(teamImage)} alt="" className="olm-team-logo" />
          <span className="olm-roster-team-name">{teamName}</span>
          <div className="olm-roster-actions">
            <span className="olm-action-link" onClick={() => selectAll(teamId)}>
              {players.every((player) => selectedPlayers[player.id]) ? t('olmDeselectAll') : t('olmSelectAll')}
            </span>
          </div>
        </div>
        <div className="olm-roster-list">
          {players.map((player) => (
            <div
              key={player.id}
              className={`olm-roster-item ${selectedPlayers[player.id] ? 'olm-roster-item-selected' : ''}`}
              onClick={() => togglePlayer(player.id)}
            >
              <span className="olm-roster-number">{player.player_number || '—'}</span>
              <span className="olm-roster-name">{player.family} {player.name} {player.isDouble === teamId ? t('mrDouble') : ''}</span>
              {player.position_id && <span className="olm-roster-pos">{t(POSITION_LABELS[player.position_id])}</span>}
              <span className="olm-roster-check">{selectedPlayers[player.id] ? '✓' : ''}</span>
            </div>
          ))}
        </div>
        <div className="olm-roster-count">{t('olmSelected', { count })}</div>
      </div>
    );
  };

  const renderRosterStep = () => (
    <div>
      <h3 className="olm-phase-title">{t('olmRosterTitle')}</h3>
      <p className="olm-phase-subtitle">{t('olmRosterSubtitle')}</p>
      {homeFromCaptain || guestFromCaptain ? <div className="gm-cap-banner">{t('gmCaptainLineupHint')}</div> : null}
      <div className="olm-roster-columns">
        {renderTeamRoster(homeTeamId, matchData.home_team_name, matchData.home_team_image)}
        {renderTeamRoster(guestTeamId, matchData.guest_team_name, matchData.guest_team_image)}
      </div>
      <div className="olm-phase-actions">
        <div className="olm-btn olm-btn-secondary" onClick={onCancel}>{cancelLabel || t('cancel')}</div>
        <div className="olm-btn olm-btn-start" onClick={goToLineup}>{t('olmNext')}</div>
      </div>
    </div>
  );

  const renderLineupField = (side, formation) => {
    const lineup = getLineup(side);
    const teamColor = side === 'home' ? homeColor : guestColor;
    const bench = getBenchPlayers(side);

    const handleLineupDrop = (slotIndex) => {
      if (!draggingPlayer || draggingPlayer.fromSide !== side) {
        setDragOverTarget(null);
        return;
      }
      assignPlayerToSlot(side, slotIndex, draggingPlayer.id);
      setDraggingPlayer(null);
      setDragOverTarget(null);
    };

    if (!formation) {
      return (
        <div className="olm-lineup-team-body">
          <div className={`olm-lineup-player-panel ${side === 'guest' ? 'olm-lineup-panel-right' : ''}`}>
            <div className="olm-lineup-panel-header">{t('olmPlayers', { count: bench.length })}</div>
            <div className="olm-lineup-panel-list">
              {bench.map((player) => {
                const posLabel = player.position_id ? t(POSITION_LABELS[player.position_id]) : '';
                return (
                  <div key={player.id} className="olm-lineup-player-card">
                    <div className="olm-lineup-card-number" style={{ background: teamColor }}>{player.player_number || '—'}</div>
                    <div className="olm-lineup-card-info">
                      <span className="olm-lineup-card-name">{player.family} {player.name}</span>
                      {posLabel && <span className="olm-lineup-card-pos">{posLabel}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="olm-lineup-field-col">
            <div className="olm-field olm-field-lineup">
              <span style={{ color: 'rgba(255,255,255,0.5)', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 13 }}>{t('olmSelectFormation')}</span>
            </div>
          </div>
        </div>
      );
    }

    const positions = generateFieldPositions(formation);

    const playerListPanel = (
      <div className={`olm-lineup-player-panel ${side === 'guest' ? 'olm-lineup-panel-right' : ''}`}>
        <div className="olm-lineup-panel-header">
          {pickingSlot && pickingSlot.teamSide === side
            ? <span className="olm-lineup-panel-picking">{t('olmPickSlot', { slot: pickingSlot.slotIndex === 0 ? 'GK' : t('olmPos', { n: pickingSlot.slotIndex }) })}</span>
            : <span>{t('olmPlayers', { count: bench.length })}</span>
          }
        </div>
        <div className="olm-lineup-panel-list">
          {bench.map((player) => {
            const posLabel = player.position_id ? t(POSITION_LABELS[player.position_id]) : '';
            const isPicking = pickingSlot && pickingSlot.teamSide === side;
            return (
              <div
                key={player.id}
                className={`olm-lineup-player-card ${isPicking ? 'olm-lineup-player-card-active' : ''}`}
                draggable
                onDragStart={() => setDraggingPlayer({ id: player.id, fromSide: side, fromBench: true })}
                onDragEnd={() => { setDraggingPlayer(null); setDragOverTarget(null); }}
                onClick={() => { if (isPicking) assignPlayerToSlot(side, pickingSlot.slotIndex, player.id); }}
                style={{ borderLeftColor: isPicking ? teamColor : 'transparent' }}
              >
                <div className="olm-lineup-card-number" style={{ background: teamColor }}>{player.player_number || '—'}</div>
                <div className="olm-lineup-card-info">
                  <span className="olm-lineup-card-name">{player.family} {player.name}</span>
                  {posLabel && <span className="olm-lineup-card-pos">{posLabel}</span>}
                </div>
              </div>
            );
          })}
          {bench.length === 0 && (
            <div className="olm-lineup-panel-empty">{t('olmAllOnField')}</div>
          )}
        </div>
      </div>
    );

    const fieldEl = (
      <div className="olm-lineup-field-col">
        <div className="olm-lineup-actions-row">
          <span className="olm-action-link" onClick={() => autoFillLineup(side)}>{t('olmAuto')}</span>
          <span className="olm-action-link" onClick={() => { setLineupSide(side, {}); setPickingSlot(null); }}>{t('olmClear')}</span>
        </div>
        <div className={`olm-field olm-field-lineup ${fieldDensityClass(formation)}`} onDragOver={(e) => e.preventDefault()}>
          <div className="olm-field-half-line" />
          <div className="olm-field-center-circle" />
          <div className="olm-field-penalty-area olm-field-penalty-bottom" />
          <div className="olm-field-penalty-area olm-field-penalty-top" />
          {positions.map((pos, i) => {
            const playerId = lineup[i];
            const player = playerId ? findPlayerById(playerId) : null;
            const isPickTarget = pickingSlot && pickingSlot.teamSide === side && pickingSlot.slotIndex === i;
            const isDragOver = dragOverTarget && dragOverTarget.side === side && dragOverTarget.slotIndex === i;
            return (
              <div
                key={i}
                className={`olm-field-slot ${player ? 'olm-field-slot-filled' : 'olm-field-slot-empty'} ${isPickTarget ? 'olm-field-slot-picking' : ''} ${isDragOver && !player ? 'olm-field-slot-drag-over' : ''}`}
                style={{ top: `${pos.top}%`, left: `${pos.left}%`, background: player ? (i === 0 ? '#f39c12' : teamColor) : 'transparent' }}
                onClick={() => handleSlotClick(side, i)}
                onDragOver={(e) => { e.preventDefault(); setDragOverTarget({ side, slotIndex: i }); }}
                onDragLeave={() => setDragOverTarget(null)}
                onDrop={() => handleLineupDrop(i)}
                draggable={!!player}
                onDragStart={() => player && setDraggingPlayer({ id: player.id, fromSide: side, fromBench: false })}
              >
                {player ? (
                  <>
                    <span className="olm-field-player-pos">{getPositionShort(player, i)}</span>
                    <span className="olm-field-player-number">{player.player_number || ''}</span>
                    <span className="olm-field-player-name">{player.family}</span>
                  </>
                ) : (
                  <span className="olm-field-slot-label">{i === 0 ? 'GK' : i}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className={`olm-lineup-team-body ${side === 'guest' ? 'olm-lineup-body-reverse' : ''}`}>
        {playerListPanel}
        {fieldEl}
      </div>
    );
  };

  const renderLineupStep = () => {
    const formations = matchFormat ? (FORMATIONS_BY_FORMAT[matchFormat] || []) : [];
    return (
      <div>
        <h3 className="olm-phase-title">{t('olmLineupTitle')}</h3>
        <p className="olm-phase-subtitle">{t('olmLineupSubtitle')}</p>
        {homeFromCaptain || guestFromCaptain ? <div className="gm-cap-banner">{t('gmCaptainLineupHint')}</div> : null}

        <div className="olm-color-pickers">
          <div className="olm-color-picker-item">
            <label>{t('olmKitColor', { team: matchData.home_team_name })}</label>
            <input type="color" value={homeColor} onChange={(e) => onChange({ homeColor: e.target.value })} />
          </div>
          <div className="olm-color-picker-item">
            <label>{t('olmKitColor', { team: matchData.guest_team_name })}</label>
            <input type="color" value={guestColor} onChange={(e) => onChange({ guestColor: e.target.value })} />
          </div>
        </div>

        <div className="olm-format-section">
          <label className="olm-format-label-text">{t('olmMatchFormat')}</label>
          <div className="olm-format-row">
            {MATCH_FORMATS.map((format) => (
              <div
                key={format}
                className={`olm-btn ${matchFormat === format ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                style={{ padding: '6px 14px', fontSize: 13 }}
                onClick={() => onFormatChange(format)}
              >
                {format}
              </div>
            ))}
          </div>
        </div>

        {matchFormat && (
          <div className="olm-lineup-columns">
            {['home', 'guest'].map((side) => {
              const teamName = side === 'home' ? matchData.home_team_name : matchData.guest_team_name;
              const formation = getFormation(side);
              return (
                <div key={side} className="olm-lineup-team">
                  <div className="olm-lineup-team-name">
                    {teamName}
                    {(side === 'home' ? homeFromCaptain : guestFromCaptain) ? <span className="gm-cap-tag">{t('gmCaptainLineup')}</span> : null}
                  </div>
                  <div className="olm-format-row" style={{ marginBottom: 8, justifyContent: 'center' }}>
                    {formations.map((item) => (
                      <div
                        key={item}
                        className={`olm-btn ${formation === item ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                        style={{ padding: '5px 10px', fontSize: 12 }}
                        onClick={() => onFormationChange(side, item)}
                      >
                        {item}
                      </div>
                    ))}
                  </div>
                  {renderLineupField(side, formation)}
                </div>
              );
            })}
          </div>
        )}

        <div className="olm-phase-actions">
          <div className="olm-btn olm-btn-secondary" onClick={() => { onStepChange('roster'); setPickingSlot(null); }}>{t('olmBack')}</div>
          <div className="olm-btn olm-btn-start" onClick={submit}>{submitLabel || t('olmStartMatch')}</div>
        </div>
      </div>
    );
  };

  if (!matchData) return null;
  return step === 'roster' ? renderRosterStep() : renderLineupStep();
}
